import { assign, fromCallback, setup } from 'xstate';
import type { AnyActorRef } from 'xstate';

import type { TripLegConfidenceRequest } from '@/data/requests/trip-leg-confidence';
import type { TripLeg } from '@/data/models/reservation.model';
import tripLegUtils from '@/services/triplegUtils';

import type { TripArrayFieldContext } from './createTripArrayFieldMachine';

export type TripLegAddressConfidenceInput = {
  pickupAddressRef: AnyActorRef;
  dropoffAddressRef: AnyActorRef;
};

export type TripLegAddressConfidencePayloadItem = {
  tripIndex: number;
  payload: TripLegConfidenceRequest;
};

export type TripLegAddressConfidenceContext = {
  pickupAddressRef: AnyActorRef;
  dropoffAddressRef: AnyActorRef;
  /** Keys `${index}\0${pickup}\0${dropoff}` */
  validatedPairKeys: Record<string, true>;
  pendingConfidencePayloads: TripLegAddressConfidencePayloadItem[];
};

/** Normalize machine-stored address (string or Google Places object) to a single line for confidence keys and payloads. */
function addressMachineValueToLineString(val: unknown): string {
  if (val == null) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'object') {
    const p = val as {
      isHeader?: boolean;
      description?: string;
      mainText?: string;
      secondaryText?: string;
      zipCode?: string;
    };
    if (p.isHeader) return p.description ?? '';
    if (p.mainText) {
      const street = p.mainText;
      let city = p.secondaryText || '';
      const zip = p.zipCode;
      if (zip) city = city.replace(` ${zip}`, '');
      return `${street} ${city} ${zip || ''}`.trim();
    }
    return `${p.description ?? ''} ${p.zipCode ?? ''}`.trim();
  }
  return '';
}

function readTripArrayValues(ref: AnyActorRef): unknown[] {
  const ctx = ref.getSnapshot().context as TripArrayFieldContext;
  return ctx.values.length ? ctx.values : [''];
}

function pairKey(index: number, pickup: string, dropoff: string): string {
  return `${index}\0${pickup}\0${dropoff}`;
}

function tripLegPartialFromAddressStrings(
  pickup: string,
  dropoff: string,
): Partial<TripLeg> {
  return {
    pickUpAddress: {
      addressLine1: pickup,
      addressLine2: '',
      city: '',
      stateCode: '',
      zipCode: '',
    },
    dropOffAddress: {
      addressLine1: dropoff,
      addressLine2: '',
      city: '',
      stateCode: '',
      zipCode: '',
    },
  };
}

function collectNewConfidencePayloads(
  context: TripLegAddressConfidenceContext,
): Pick<TripLegAddressConfidenceContext, 'validatedPairKeys' | 'pendingConfidencePayloads'> {
  const pickups = readTripArrayValues(context.pickupAddressRef);
  const dropoffs = readTripArrayValues(context.dropoffAddressRef);
  const len = Math.max(pickups.length, dropoffs.length, 1);
  const validatedPairKeys = { ...context.validatedPairKeys };
  const pendingConfidencePayloads: TripLegAddressConfidencePayloadItem[] = [];

  for (let i = 0; i < len; i++) {
    const pickup = addressMachineValueToLineString(pickups[i]);
    const dropoff = addressMachineValueToLineString(dropoffs[i]);
    if (!pickup.trim() || !dropoff.trim()) {
      continue;
    }
    const key = pairKey(i, pickup, dropoff);
    if (validatedPairKeys[key]) {
      continue;
    }
    validatedPairKeys[key] = true;
    const payload = tripLegUtils.getTripLegConfidencePayload(
      tripLegPartialFromAddressStrings(pickup, dropoff),
    );
    pendingConfidencePayloads.push({ tripIndex: i, payload });
  }

  return { validatedPairKeys, pendingConfidencePayloads };
}

const addressPairSubscription = fromCallback(
  ({
    sendBack,
    input,
  }: {
    sendBack: (event: { type: 'ADDRESSES_UPDATED' }) => void;
    input: TripLegAddressConfidenceInput;
  }) => {
    const emit = () => {
      sendBack({ type: 'ADDRESSES_UPDATED' });
    };
    const sub1 = input.pickupAddressRef.subscribe(emit);
    const sub2 = input.dropoffAddressRef.subscribe(emit);
    emit();
    return () => {
      sub1.unsubscribe();
      sub2.unsubscribe();
    };
  },
);

export const tripLegAddressConfidenceMachine = setup({
  types: {
    context: {} as TripLegAddressConfidenceContext,
    events: {} as
      | { type: 'ADDRESSES_UPDATED' }
      | { type: 'RESET' }
      | { type: 'CLEAR_PENDING' },
    input: {} as TripLegAddressConfidenceInput,
  },
  actors: {
    addressPairSubscription,
  },
}).createMachine({
  id: 'tripLegAddressConfidence',
  context: ({ input }) => ({
    pickupAddressRef: input.pickupAddressRef,
    dropoffAddressRef: input.dropoffAddressRef,
    validatedPairKeys: {},
    pendingConfidencePayloads: [],
  }),
  initial: 'watching',
  states: {
    watching: {
      invoke: {
        id: 'addressPairSubscription',
        src: 'addressPairSubscription',
        input: ({ context }) => ({
          pickupAddressRef: context.pickupAddressRef,
          dropoffAddressRef: context.dropoffAddressRef,
        }),
      },
      on: {
        ADDRESSES_UPDATED: {
          actions: assign(({ context }) => {
            const next = collectNewConfidencePayloads(context);
            return {
              validatedPairKeys: next.validatedPairKeys,
              pendingConfidencePayloads: [
                ...context.pendingConfidencePayloads,
                ...next.pendingConfidencePayloads,
              ],
            };
          }),
        },
        CLEAR_PENDING: {
          actions: assign({ pendingConfidencePayloads: () => [] }),
        },
        RESET: {
          actions: assign({
            validatedPairKeys: () => ({}),
            pendingConfidencePayloads: () => [],
          }),
        },
      },
    },
  },
});
